/**
 * Graphics capability detection — decides the render backend at startup.
 *
 * Policy (Step 2 consumes this):
 *  - WebGPU when `navigator.gpu` exists AND requestAdapter() actually
 *    succeeds. Safari 17.4+ ships WebGPU behind a flag and 26 enables it by
 *    default, but WebKit can expose navigator.gpu while denying adapters
 *    (e.g. lockdown mode), so we must await the adapter, not just sniff.
 *  - Otherwise WebGL 2 (Safari 15+, all modern browsers).
 * Three.js's WebGPURenderer handles per-feature fallback internally; this
 * module only picks the top-level backend and records limits we care about.
 */

export interface GraphicsCapabilities {
  backend: 'webgpu' | 'webgl2' | 'none';
  /** Filled in when backend === 'webgpu'. */
  adapterInfo?: { vendor: string; architecture: string };
  maxTextureSize: number;
  /** SharedArrayBuffer available (cross-origin isolated) — enables threaded WASM. */
  crossOriginIsolated: boolean;
}

export async function detectGraphics(): Promise<GraphicsCapabilities> {
  const crossOriginIsolated = globalThis.crossOriginIsolated === true;

  // --- WebGPU probe (must actually acquire an adapter) ---
  try {
    const gpu = (navigator as Navigator & { gpu?: GPU }).gpu;
    if (gpu) {
      const adapter = await gpu.requestAdapter();
      if (adapter) {
        const info = adapter.info;
        return {
          backend: 'webgpu',
          adapterInfo: {
            vendor: info?.vendor ?? 'unknown',
            architecture: info?.architecture ?? 'unknown',
          },
          maxTextureSize: adapter.limits.maxTextureDimension2D,
          crossOriginIsolated,
        };
      }
    }
  } catch {
    /* fall through to WebGL2 */
  }

  // --- WebGL2 probe ---
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: false });
  if (gl) {
    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return { backend: 'webgl2', maxTextureSize, crossOriginIsolated };
  }

  return { backend: 'none', maxTextureSize: 0, crossOriginIsolated };
}
